/**
 * Analytics Event Constants
 *
 * Type-safe event definitions for funnel tracking, engagement, and drop-off analysis.
 * Used with Mixpanel for comprehensive user journey analytics.
 *
 * @see https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript
 * @version 1.0.0 - November 2025
 */

// =============================================================================
// FUNNEL EVENTS - Track the complete user journey
// =============================================================================

/**
 * Funnel event names for tracking the conversion funnel.
 * Format: "Funnel: [Step Name]" for easy filtering in Mixpanel.
 */
export const FUNNEL_EVENTS = {
  // Landing & Entry
  LANDING_VIEWED: 'Funnel: Landing Viewed',
  WORKSHOP_STARTED: 'Funnel: Workshop Started',

  // Input Method Selection
  INPUT_METHOD_SELECTED: 'Funnel: Input Method Selected',

  // Generation Flow
  GENERATION_STARTED: 'Funnel: Generation Started',
  GENERATION_COMPLETED: 'Funnel: Generation Completed',
  GENERATION_FAILED: 'Funnel: Generation Failed',

  // Design Selection
  VARIANT_SELECTED: 'Funnel: Variant Selected',
  PRODUCT_CONFIGURED: 'Funnel: Product Configured',

  // Cart Flow
  CART_VIEWED: 'Funnel: Cart Viewed',
  CART_ITEM_ADDED: 'Funnel: Cart Item Added',
  CART_ITEM_REMOVED: 'Funnel: Cart Item Removed',
  CART_ITEM_UPDATED: 'Funnel: Cart Item Updated',

  // Checkout Flow
  CHECKOUT_STARTED: 'Funnel: Checkout Started',
  CHECKOUT_STEP_COMPLETED: 'Funnel: Checkout Step Completed',

  // Purchase
  PURCHASE_COMPLETED: 'Funnel: Purchase Completed',
  PURCHASE_FAILED: 'Funnel: Purchase Failed',
} as const;

export type FunnelEventName = (typeof FUNNEL_EVENTS)[keyof typeof FUNNEL_EVENTS];

// =============================================================================
// ENGAGEMENT EVENTS - Track user interactions
// =============================================================================

/**
 * Engagement event names for tracking user interactions.
 * Format: "Engagement: [Action]" for easy filtering.
 */
export const ENGAGEMENT_EVENTS = {
  // CTA Interactions
  CTA_CLICKED: 'Engagement: CTA Clicked',

  // Gallery & Inspiration
  INSPIRATION_VIEWED: 'Engagement: Inspiration Viewed',
  INSPIRATION_ITEM_CLICKED: 'Engagement: Inspiration Item Clicked',

  // Quick Actions
  SURPRISE_ME_CLICKED: 'Engagement: Surprise Me Clicked',

  // Social & Sharing
  DESIGN_SHARED: 'Engagement: Design Shared',

  // Marketing
  EMAIL_CAPTURED: 'Engagement: Email Captured',
  EMAIL_MODAL_SHOWN: 'Engagement: Email Modal Shown',
  EMAIL_MODAL_DISMISSED: 'Engagement: Email Modal Dismissed',

  // Feature Discovery
  FEATURE_DISCOVERED: 'Engagement: Feature Discovered',

  // Cross-Sell & Add-Ons
  CROSS_SELL_VIEWED: 'Engagement: Cross-Sell Viewed',
  CROSS_SELL_ADDED: 'Engagement: Cross-Sell Added',
  ADDONS_VIEWED: 'Engagement: Add-Ons Viewed',
  ADDON_ADDED: 'Engagement: Add-On Added',
  ADDON_REMOVED: 'Engagement: Add-On Removed',

  // Retention
  CONTINUE_DESIGN_SHOWN: 'Engagement: Continue Design Shown',
  CONTINUE_DESIGN_CLICKED: 'Engagement: Continue Design Clicked',
  CONTINUE_DESIGN_DISMISSED: 'Engagement: Continue Design Dismissed',
} as const;

export type EngagementEventName =
  (typeof ENGAGEMENT_EVENTS)[keyof typeof ENGAGEMENT_EVENTS];

// =============================================================================
// DROP-OFF EVENTS - Track abandonment points
// =============================================================================

/**
 * Drop-off event names for tracking where users abandon the flow.
 * Format: "Drop-off: [Stage]" for easy filtering.
 */
export const DROP_OFF_EVENTS = {
  // Design Abandonment
  DESIGN_ABANDONED: 'Drop-off: Design Abandoned',

  // Cart Abandonment
  CART_ABANDONED: 'Drop-off: Cart Abandoned',

  // Checkout Abandonment
  CHECKOUT_ABANDONED: 'Drop-off: Checkout Abandoned',

  // Session Timeout
  SESSION_EXPIRED: 'Drop-off: Session Expired',
} as const;

export type DropOffEventName =
  (typeof DROP_OFF_EVENTS)[keyof typeof DROP_OFF_EVENTS];

// =============================================================================
// EVENT PROPERTY INTERFACES
// =============================================================================

/**
 * Base properties included with every event.
 * Automatically enriched by useAnalytics hook.
 */

// -----------------------------------------------------------------------------
// Funnel Event Properties
// -----------------------------------------------------------------------------

/** Properties for LANDING_VIEWED event */
interface LandingViewedProperties {
  /** Where the user came from */
  referrer?: string;
  /** UTM source parameter */
  utm_source?: string;
  /** UTM medium parameter */
  utm_medium?: string;
  /** UTM campaign parameter */
  utm_campaign?: string;
  /** UTM content parameter */
  utm_content?: string;
  /** UTM term parameter */
  utm_term?: string;
}

/** Properties for WORKSHOP_STARTED event */
interface WorkshopStartedProperties {
  /** Whether user has an existing session */
  has_existing_session: boolean;
  /** Previous page before entering workshop */
  referrer?: string;
  /** Entry point (direct, landing, studio, etc.) */
  entry_point?: string;
}

/** Properties for INPUT_METHOD_SELECTED event */
interface InputMethodSelectedProperties {
  /** The input method chosen */
  input_method: 'photo' | 'prompt' | 'wizard' | 'selections' | 'ai_assistant';
  /** Time spent on method selection (ms) */
  time_to_select_ms?: number;
}

/** Properties for GENERATION_STARTED event */
interface GenerationStartedProperties {
  /** The input method used */
  input_method: 'photo' | 'prompt' | 'wizard' | 'selections' | 'ai_assistant';
  /** Whether a photo was uploaded */
  has_photo: boolean;
  /** Length of prompt text (if applicable) */
  prompt_length?: number;
  /** Photo asset ID (if applicable) */
  photo_asset_id?: string;
}

/** Properties for GENERATION_COMPLETED event */
interface GenerationCompletedProperties {
  /** Number of variants generated */
  variant_count: number;
  /** Time taken for generation (ms) */
  generation_time_ms: number;
  /** Whether generation was successful */
  success: boolean;
  /** Generation session ID */
  session_id?: string;
  /** Input method used */
  input_method?: string;
  /** Subject type detected (human, animal, etc.) */
  subject_type?: string;
}

/** Properties for GENERATION_FAILED event */
interface GenerationFailedProperties {
  /** Error message */
  error_message: string;
  /** Error code (if available) */
  error_code?: string;
  /** Input method used */
  input_method?: string;
  /** At what stage did it fail */
  failure_stage?: string;
}

/** Properties for VARIANT_SELECTED event */
interface VariantSelectedProperties {
  /** Index of the selected variant (0-based) */
  variant_index: number;
  /** Variant ID */
  variant_id?: string;
  /** Time spent selecting (ms) */
  time_to_select_ms?: number;
  /** Total variants available */
  total_variants?: number;
}

/** Properties for PRODUCT_CONFIGURED event */
interface ProductConfiguredProperties {
  /** Type of product */
  product_type: string;
  /** Selected size */
  size?: string;
  /** Selected color */
  color?: string;
  /** Product price */
  price: number;
  /** Currency */
  currency: string;
  /** Product ID */
  product_id?: string;
}

/** Properties for CART_VIEWED event */
interface CartViewedProperties {
  /** Number of items in cart */
  item_count: number;
  /** Total cart value */
  total_value: number;
  /** Currency */
  currency: string;
}

/** Properties for CART_ITEM_ADDED event */
interface CartItemAddedProperties {
  /** Product ID */
  product_id: string;
  /** Design/variant ID */
  design_id?: string;
  /** Item price */
  price: number;
  /** Currency */
  currency: string;
  /** Product type */
  product_type?: string;
  /** Source of add (product page, cross-sell, etc.) */
  source?: string;
}

/** Properties for CART_ITEM_REMOVED event */
interface CartItemRemovedProperties {
  /** Product ID */
  product_id: string;
  /** Reason for removal (if provided) */
  reason?: 'user_removed' | 'out_of_stock' | 'expired' | 'other';
  /** Item price */
  price?: number;
}

/** Properties for CART_ITEM_UPDATED event */
interface CartItemUpdatedProperties {
  /** Product ID */
  product_id: string;
  /** Old quantity */
  old_quantity: number;
  /** New quantity */
  new_quantity: number;
  /** Item price */
  price?: number;
  /** Currency */
  currency?: string;
}

/** Properties for CHECKOUT_STARTED event */
interface CheckoutStartedProperties {
  /** Total cart value */
  cart_total: number;
  /** Number of items */
  item_count: number;
  /** Currency */
  currency: string;
  /** Whether user is authenticated */
  is_authenticated?: boolean;
}

/** Properties for CHECKOUT_STEP_COMPLETED event */
interface CheckoutStepCompletedProperties {
  /** Step name */
  step_name: 'shipping' | 'payment' | 'review';
  /** Time spent on step (ms) */
  time_on_step_ms?: number;
  /** Cart total at this step */
  cart_total?: number;
  /** Payment method used (if step is payment) */
  payment_method?: string;
  /** Express checkout type (apple_pay, google_pay) */
  express_checkout_type?: 'apple_pay' | 'google_pay';
}

/** Properties for PURCHASE_COMPLETED event */
interface PurchaseCompletedProperties {
  /** Order ID */
  order_id?: string;
  /** Total amount */
  total?: number;
  /** Payment method used */
  payment_method: string;
  /** Currency */
  currency?: string;
  /** Number of items */
  item_count?: number;
  /** Whether order includes add-ons */
  has_add_ons?: boolean;
  /** Discount code used (if any) */
  discount_code?: string;
  /** Discount amount */
  discount_amount?: number;
  /** Express checkout type (apple_pay, google_pay) */
  express_checkout_type?: 'apple_pay' | 'google_pay';
}

/** Properties for PURCHASE_FAILED event */
interface PurchaseFailedProperties {
  /** Error message */
  error_message: string;
  /** At what step did it fail */
  step: string;
  /** Cart total */
  cart_total?: number;
  /** Payment method attempted */
  payment_method?: string;
  /** Express checkout type (apple_pay, google_pay) */
  express_checkout_type?: 'apple_pay' | 'google_pay';
}

// -----------------------------------------------------------------------------
// Engagement Event Properties
// -----------------------------------------------------------------------------

/** Properties for CTA_CLICKED event */
interface CTAClickedProperties {
  /** Name of the CTA */
  cta_name: string;
  /** Location of the CTA on the page */
  cta_location: string;
  /** Text on the CTA (if applicable) */
  cta_text?: string;
  /** Destination URL (if applicable) */
  destination_url?: string;
}

/** Properties for INSPIRATION_VIEWED event */
interface InspirationViewedProperties {
  /** Position in the gallery */
  gallery_position?: number;
  /** Number of items in gallery */
  gallery_size?: number;
  /** How user reached gallery (scroll, click, etc.) */
  trigger?: string;
}

/** Properties for INSPIRATION_ITEM_CLICKED event */
interface InspirationItemClickedProperties {
  /** Item index in gallery */
  item_index: number;
  /** Item type (3D print, plush, etc.) */
  item_type?: string;
  /** Item caption */
  item_caption?: string;
}

/** Properties for SURPRISE_ME_CLICKED event */
interface SurpriseMeClickedProperties {
  /** The prompt that was selected */
  selected_prompt: string;
  /** Index of prompt in the list */
  prompt_index?: number;
}

/** Properties for DESIGN_SHARED event */
interface DesignSharedProperties {
  /** Platform shared to */
  platform: 'facebook' | 'twitter' | 'pinterest' | 'email' | 'copy_link' | 'other';
  /** Design ID */
  design_id?: string;
}

/** Properties for EMAIL_CAPTURED event */
interface EmailCapturedProperties {
  /** Source of capture */
  source: 'exit_intent' | 'footer' | 'checkout' | 'popup' | 'other';
  /** Whether user opted into marketing */
  marketing_opt_in?: boolean;
}

/** Properties for CROSS_SELL_VIEWED event */
interface CrossSellViewedProperties {
  /** Number of products shown */
  products_shown: number;
  /** Product IDs shown */
  product_ids: string[];
}

/** Properties for CROSS_SELL_ADDED event */
interface CrossSellAddedProperties {
  /** Product ID added */
  product_id: string;
  /** Product name */
  product_name: string;
  /** Price in minor units */
  price: number;
  /** Currency */
  currency: string;
}

/** Properties for ADDONS_VIEWED event */
interface AddOnsViewedProperties {
  /** Number of add-ons available */
  addons_available: number;
}

/** Properties for ADDON_ADDED/REMOVED events */
interface AddOnChangedProperties {
  /** Add-on ID */
  addon_id: string;
  /** Add-on name */
  addon_name: string;
  /** Price in minor units */
  addon_price: number;
}

/** Properties for CONTINUE_DESIGN_SHOWN event */
interface ContinueDesignShownProperties {
  /** Number of incomplete sessions */
  sessions_count: number;
  /** Age of oldest session in hours */
  oldest_session_age_hours?: number;
}

/** Properties for CONTINUE_DESIGN_CLICKED event */
interface ContinueDesignClickedProperties {
  /** Session ID being continued */
  session_id: string;
  /** Input method used */
  input_method: string;
  /** Session age in hours */
  session_age_hours?: number;
}

/** Properties for CONTINUE_DESIGN_DISMISSED event */
interface ContinueDesignDismissedProperties {
  /** Number of sessions available */
  sessions_count: number;
}

// -----------------------------------------------------------------------------
// Drop-off Event Properties
// -----------------------------------------------------------------------------

/** Properties for DESIGN_ABANDONED event */
interface DesignAbandonedProperties {
  /** Step where user abandoned */
  step_abandoned: string;
  /** Time spent before abandoning (ms) */
  time_spent_ms: number;
  /** Input method used */
  input_method?: string;
  /** Whether generation was in progress */
  generation_in_progress?: boolean;
}

/** Properties for CART_ABANDONED event */
interface CartAbandonedProperties {
  /** Total cart value */
  cart_total: number;
  /** Number of items */
  item_count: number;
  /** Time in cart before abandoning (ms) */
  time_in_cart_ms: number;
  /** Currency */
  currency: string;
}

/** Properties for CHECKOUT_ABANDONED event */
interface CheckoutAbandonedProperties {
  /** Step where user abandoned */
  step: 'shipping' | 'payment' | 'review';
  /** Cart total */
  cart_total: number;
  /** Time in checkout (ms) */
  time_in_checkout_ms?: number;
  /** Currency */
  currency: string;
}

// =============================================================================
// TYPE MAPS - For type-safe event tracking
// =============================================================================

/**
 * Maps funnel event names to their property types.
 * Used by useAnalytics hook for type safety.
 */
export interface FunnelEventPropertyMap {
  [FUNNEL_EVENTS.LANDING_VIEWED]: LandingViewedProperties;
  [FUNNEL_EVENTS.WORKSHOP_STARTED]: WorkshopStartedProperties;
  [FUNNEL_EVENTS.INPUT_METHOD_SELECTED]: InputMethodSelectedProperties;
  [FUNNEL_EVENTS.GENERATION_STARTED]: GenerationStartedProperties;
  [FUNNEL_EVENTS.GENERATION_COMPLETED]: GenerationCompletedProperties;
  [FUNNEL_EVENTS.GENERATION_FAILED]: GenerationFailedProperties;
  [FUNNEL_EVENTS.VARIANT_SELECTED]: VariantSelectedProperties;
  [FUNNEL_EVENTS.PRODUCT_CONFIGURED]: ProductConfiguredProperties;
  [FUNNEL_EVENTS.CART_VIEWED]: CartViewedProperties;
  [FUNNEL_EVENTS.CART_ITEM_ADDED]: CartItemAddedProperties;
  [FUNNEL_EVENTS.CART_ITEM_REMOVED]: CartItemRemovedProperties;
  [FUNNEL_EVENTS.CART_ITEM_UPDATED]: CartItemUpdatedProperties;
  [FUNNEL_EVENTS.CHECKOUT_STARTED]: CheckoutStartedProperties;
  [FUNNEL_EVENTS.CHECKOUT_STEP_COMPLETED]: CheckoutStepCompletedProperties;
  [FUNNEL_EVENTS.PURCHASE_COMPLETED]: PurchaseCompletedProperties;
  [FUNNEL_EVENTS.PURCHASE_FAILED]: PurchaseFailedProperties;
}

/**
 * Maps engagement event names to their property types.
 */
export interface EngagementEventPropertyMap {
  [ENGAGEMENT_EVENTS.CTA_CLICKED]: CTAClickedProperties;
  [ENGAGEMENT_EVENTS.INSPIRATION_VIEWED]: InspirationViewedProperties;
  [ENGAGEMENT_EVENTS.INSPIRATION_ITEM_CLICKED]: InspirationItemClickedProperties;
  [ENGAGEMENT_EVENTS.SURPRISE_ME_CLICKED]: SurpriseMeClickedProperties;
  [ENGAGEMENT_EVENTS.DESIGN_SHARED]: DesignSharedProperties;
  [ENGAGEMENT_EVENTS.EMAIL_CAPTURED]: EmailCapturedProperties;
  [ENGAGEMENT_EVENTS.EMAIL_MODAL_SHOWN]: Record<string, never>;
  [ENGAGEMENT_EVENTS.EMAIL_MODAL_DISMISSED]: Record<string, never>;
  [ENGAGEMENT_EVENTS.FEATURE_DISCOVERED]: { feature_name: string };
  [ENGAGEMENT_EVENTS.CROSS_SELL_VIEWED]: CrossSellViewedProperties;
  [ENGAGEMENT_EVENTS.CROSS_SELL_ADDED]: CrossSellAddedProperties;
  [ENGAGEMENT_EVENTS.ADDONS_VIEWED]: AddOnsViewedProperties;
  [ENGAGEMENT_EVENTS.ADDON_ADDED]: AddOnChangedProperties;
  [ENGAGEMENT_EVENTS.ADDON_REMOVED]: AddOnChangedProperties;
  [ENGAGEMENT_EVENTS.CONTINUE_DESIGN_SHOWN]: ContinueDesignShownProperties;
  [ENGAGEMENT_EVENTS.CONTINUE_DESIGN_CLICKED]: ContinueDesignClickedProperties;
  [ENGAGEMENT_EVENTS.CONTINUE_DESIGN_DISMISSED]: ContinueDesignDismissedProperties;
}

/**
 * Maps drop-off event names to their property types.
 */
export interface DropOffEventPropertyMap {
  [DROP_OFF_EVENTS.DESIGN_ABANDONED]: DesignAbandonedProperties;
  [DROP_OFF_EVENTS.CART_ABANDONED]: CartAbandonedProperties;
  [DROP_OFF_EVENTS.CHECKOUT_ABANDONED]: CheckoutAbandonedProperties;
  [DROP_OFF_EVENTS.SESSION_EXPIRED]: { time_since_last_activity_ms: number };
}

// =============================================================================
// ALL EVENTS - Combined export
// =============================================================================
